import React, { useRef, useState } from 'react';
import { Box } from '@withjoy/joykit';
import { useIsomorphicLayoutEffect } from '@shared/utils/hooks/useIsomorphicLayoutEffect';
import { createSafeResizeObserver } from '@shared/utils/createSafeResizeObserver';

type FitContentToHeightProps = Readonly<{
  /**
   * Max height of the available band, as a CSS length (e.g.
   * `calc(100vh - 12vw - 12vw)`). Content taller than this is scaled down to fit.
   * When undefined, children render as-is (no measuring/scaling).
   */
  maxHeight?: string;
  children: React.ReactNode;
}>;

/**
 * Uniformly scales its content down so it fits within `maxHeight`, leaving it
 * unscaled when it already fits. Used by the Aloha WelcomeCard to keep the
 * date/location block within the art safe zone when the user has scaled fonts
 * up on a short viewport.
 *
 * A single uniform `transform: scale` is used (rather than react-textfit on
 * each text element) because the date and location font sizes are independent
 * user settings — scaling the whole block by one factor preserves their ratio,
 * and `transform` doesn't affect layout, so the measurement can't oscillate.
 */
export const FitContentToHeight: React.FC<FitContentToHeightProps> = ({ maxHeight, children }) => {
  const outerRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  useIsomorphicLayoutEffect(() => {
    const outer = outerRef.current;
    const inner = innerRef.current;
    if (!outer || !inner) return undefined;

    const measure = () => {
      const available = outer.clientHeight;
      // scrollHeight reflects the un-transformed layout height, so this is
      // stable regardless of the scale we apply below (no feedback loop).
      const natural = inner.scrollHeight;
      setScale(natural > available && natural > 0 ? available / natural : 1);
    };

    measure();

    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = createSafeResizeObserver(measure);
    observer.observe(outer);
    observer.observe(inner);
    return () => observer.disconnect();
  }, [maxHeight]);

  if (!maxHeight) {
    return <>{children}</>;
  }

  return (
    <Box
      ref={outerRef}
      width="100%"
      display="flex"
      alignItems="center"
      justifyContent="center"
      overflow="hidden"
      style={{ maxHeight }}
    >
      <Box
        ref={innerRef}
        width="100%"
        style={{ transform: `scale(${scale})`, transformOrigin: 'center center' }}
      >
        {children}
      </Box>
    </Box>
  );
};
